import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getStudents, getStudentMonthlyLearningData } from "../../../network/Endpoint";
import MonthSelector from "../components/MonthSelector";
import StudentSelector from "../components/StudentSelector";
import StudentMonthlyReport from "../components/StudentMonthlyReport";
import { cloneDeep } from "lodash";
import { useRecoilValue } from "recoil";
import { mainClassState } from "../../../recoil/classes/classesState";
import {useReactToPrint} from "react-to-print";
import btnKakaoLink from "../../../assets/images/month_report_link.svg";
import btnPrint from "../../../assets/images/month_report_print.svg";
import SideMenu from "../../../components/SideMenu";

const StudentMonthlyResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const mainClass = useRecoilValue(mainClassState);
  
  // 데이터 로딩 상태
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showLoadingUI, setShowLoadingUI] = useState(false);
  const loadingTimerRef = useRef(null);
  
  // 현재 년월 가져오기
  const getCurrentMonth = () => {
    const now = new Date();
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1 // JavaScript는 0부터 시작하므로 +1
    };
  };

  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [studentId, setStudentId] = useState(null);
  const [students, setStudents] = useState(null);
  
  // 월간 학습 데이터 상태
  const [monthlyData, setMonthlyData] = useState(null);

  // 프린트 영역
  const contentRef = useRef(null);

  // 로딩 상태 변경 시 지연된 로딩 UI 표시 처리
  useEffect(() => {
    if (loading || initialLoading) {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
      
      loadingTimerRef.current = setTimeout(() => {
        if (loading || initialLoading) {
          setShowLoadingUI(true);
        }
      }, 300);
    } else {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setShowLoadingUI(false);
    }
    
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, [loading, initialLoading]);

  // URL 파라미터 확인 및 적용
  useEffect(() => {
    const yearParam = searchParams.get('year');
    const monthParam = searchParams.get('month');
    const urlStudentId = searchParams.get('studentId');
    
    if (yearParam && monthParam) {
      setSelectedMonth({
        year: parseInt(yearParam),
        month: parseInt(monthParam)
      });
    }
    
    if (urlStudentId) {
      setStudentId(urlStudentId);
    }
  }, [searchParams]);

  // 년월이나 studentId가 변경될 때 URL 업데이트
  useEffect(() => {
    if (selectedMonth.year && selectedMonth.month) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('year', selectedMonth.year.toString());
      newSearchParams.set('month', selectedMonth.month.toString());
      
      if (studentId) {
        newSearchParams.set('studentId', studentId);
      }
      
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [selectedMonth, studentId]);

  useEffect(() => {
    if (mainClass?.classId) {
      fetchGetStudents(mainClass.classId);
    }
  }, [mainClass]);

  // 학생 목록을 가져온 후, URL에 학생 ID가 없고 학생 목록이 있으면 첫 번째 학생 선택
  useEffect(() => {
    if (students?.length > 0 && !studentId && !searchParams.get('studentId')) {
      setStudentId(students[0].studentId);
    }
  }, [students, studentId, searchParams]);

  // studentId나 selectedMonth가 변경될 때만 데이터 가져오기
  useEffect(() => {
    if (studentId && selectedMonth.year && selectedMonth.month) {
      fetchStudentMonthlyResults();
    }
  }, [studentId, selectedMonth]);

  const fetchGetStudents = async (classId) => {
    try {
      const body = {
        classId: mainClass.classId,
      };
      const res = await getStudents(body);

      const statusCode = res.data.status.code;
      const statusMessage = res.data.status.message;
      if (statusCode === 'Success') {
        const students = res.data.data.students;
        setStudents(cloneDeep(students));
      } else {
        alert(`ERROR: 학생 정보 가져오는 중 오류가 발생했어요.\n아크수학으로 문의해 주세요.\n${statusMessage}`);
      }
    } catch (error) {
      alert('학생 정보 가져오는 중 오류가 발생했어요.\n아크수학으로 문의해 주세요.');
    }
  };

  // API 호출하여 학생 월간 결과 데이터 가져오기
  const fetchStudentMonthlyResults = async () => {
    if (!studentId || !selectedMonth.year || !selectedMonth.month) return;
    
    setLoading(true);

    try {
      const body = {
        studentId: studentId,
        params: {
          year: selectedMonth.year,
          month: selectedMonth.month
        }
      };
      
      const res = await getStudentMonthlyLearningData(body);
      
      if (res.data && res.data.status.code === "Success") {
        const responseData = res.data.data;
        setMonthlyData(responseData);
        console.log("월간 데이터 로딩 완료:", responseData);
      } else {
        console.error('API 응답 형식이 올바르지 않습니다:', res.data);
      }
    } catch (error) {
      console.error('학생 월간 결과를 가져오는 중 오류 발생:', error);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  // 학생 변경 핸들러
  const handleStudentChange = (newStudentId) => {
    setStudentId(newStudentId);
  };

  // 월간 변경 핸들러
  const handleMonthChange = (newMonth) => {
    setSelectedMonth(newMonth);
  };

  // 인쇄 핸들러
  const handlePrint = useReactToPrint({
    content: () => contentRef.current,
  });

  // 현재 선택된 학생 정보 가져오기
  const getCurrentStudent = () => {
    return students?.find(student => String(student.studentId) === String(studentId));
  };

  // 보안 토큰 생성 함수
  const generateSecureToken = (studentId, year, month) => {
    const data = `${studentId}-${year}-${month}`;
    const timestamp = Math.floor(Date.now() / 1000); // 현재 시간 (초)
    const secret = process.env.REACT_APP_REPORT_SECRET || 'arkmath_secret_2024'; // 환경변수에서 시크릿 가져오기
    
    // 간단한 해시 생성 (실제 프로덕션에서는 더 강력한 암호화 사용 권장)
    const combinedData = `${data}-${timestamp}-${secret}`;
    const hash = btoa(combinedData).replace(/[+=\/]/g, ''); // Base64 인코딩 후 특수문자 제거
    
    return `${hash}-${timestamp}`;
  };

  // 공유 링크 생성 및 클립보드 복사
  const handleShareLink = async () => {
    const currentStudent = getCurrentStudent();
    if (!currentStudent || !selectedMonth.year || !selectedMonth.month) {
      alert('공유 링크를 생성할 수 없습니다.');
      return;
    }

    const secureToken = generateSecureToken(studentId, selectedMonth.year, selectedMonth.month);
    const shareUrl = new URL('/share/studentMonthlyReport', window.location.origin);
    shareUrl.searchParams.set('token', secureToken);

    try {
      await navigator.clipboard.writeText(shareUrl.toString());
      alert('공유 링크가 클립보드에 복사되었습니다!');
    } catch (err) {
      console.error('클립보드 복사 실패:', err);
      alert('클립보드 복사에 실패했습니다. 링크: ' + shareUrl.toString());
    }
  };

  return (
    <>
      {/* 인쇄용 스타일 */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { 
            position: absolute; 
            left: 0; 
            top: 0; 
            width: 100%;
            background: white !important;
          }
          .print\\:hidden { display: none !important; }
          .print\\:px-0 { padding-left: 0 !important; padding-right: 0 !important; }
          .print\\:max-w-none { max-width: none !important; }
          @page { 
            margin: 0.5in;
            size: A4;
          }
        }
      `}</style>
      
      <div className="min-h-screen bg-gray-50 relative">
        <div className="print:hidden">
          <StudentSelector 
            students={students}
            selectedStudentId={studentId}
            onStudentChange={handleStudentChange}
          />
          <MonthSelector
            selectedMonth={selectedMonth}
            onMonthChange={handleMonthChange}
          />
        </div>
      
      {/* 로딩 UI */}
      {showLoadingUI && (
        <div className="fixed inset-0 bg-white bg-opacity-70 flex justify-center items-center z-50">
          <div className="flex flex-col items-center bg-white p-6 rounded-lg shadow-md">
            <div className="w-12 h-12 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-700">월간 학습 데이터를 불러오는 중...</p>
          </div>
        </div>
      )}
      
      {/* 인쇄 및 공유 버튼 */}
      {!initialLoading && monthlyData && (
          <div className="flex justify-end mb-3 space-x-4 print:hidden w-[1040px] mx-auto ">
            <button
                onClick={handleShareLink}
                className="py-[10px] rounded-[200px] flex items-center transition-colors bg-[#C0C0C0] px-4 hover:bg-[#949494]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <g clip-path="url(#clip0_1512_32895)">
                  <path
                      d="M8.33203 10.83C8.68991 11.3085 9.1465 11.7044 9.67082 11.9908C10.1952 12.2773 10.775 12.4476 11.3709 12.4903C11.9669 12.533 12.565 12.447 13.1248 12.2382C13.6846 12.0294 14.193 11.7026 14.6154 11.28L17.1154 8.78005C17.8744 7.99421 18.2943 6.9417 18.2848 5.84921C18.2753 4.75672 17.8371 3.71167 17.0646 2.93914C16.2921 2.1666 15.247 1.7284 14.1545 1.71891C13.062 1.70941 12.0095 2.12939 11.2237 2.88838L9.79036 4.31338"
                      stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                  <path
                      d="M11.6639 9.16702C11.306 8.68858 10.8494 8.2927 10.3251 8.00623C9.80078 7.71977 9.22098 7.54942 8.62503 7.50674C8.02908 7.46406 7.43091 7.55004 6.87112 7.75887C6.31132 7.96769 5.80298 8.29446 5.38057 8.71702L2.88057 11.217C2.12158 12.0029 1.7016 13.0554 1.7111 14.1479C1.72059 15.2403 2.15879 16.2854 2.93133 17.0579C3.70386 17.8305 4.74891 18.2687 5.8414 18.2782C6.93389 18.2877 7.98639 17.8677 8.77224 17.1087L10.1972 15.6837"
                      stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_1512_32895">
                    <rect width="20" height="20" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
              <span className="ml-2 text-[16px] text-[#fff] font-[700]">카카오톡 링크</span>
            </button>
            <button
                onClick={handlePrint}
                className="py-[10px] rounded-[200px] flex items-center transition-colors bg-[#C0C0C0] px-4 hover:bg-[#949494]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <g clip-path="url(#clip0_1512_32900)">
                  <path d="M5 7.4974V1.66406H15V7.4974" stroke="white" stroke-width="1.66667" stroke-linecap="round"
                        stroke-linejoin="round"/>
                  <path
                      d="M5.0013 15H3.33464C2.89261 15 2.46868 14.8244 2.15612 14.5118C1.84356 14.1993 1.66797 13.7754 1.66797 13.3333V9.16667C1.66797 8.72464 1.84356 8.30072 2.15612 7.98816C2.46868 7.6756 2.89261 7.5 3.33464 7.5H16.668C17.11 7.5 17.5339 7.6756 17.8465 7.98816C18.159 8.30072 18.3346 8.72464 18.3346 9.16667V13.3333C18.3346 13.7754 18.159 14.1993 17.8465 14.5118C17.5339 14.8244 17.11 15 16.668 15H15.0013"
                      stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M15 11.6641H5V18.3307H15V11.6641Z" stroke="white" stroke-width="1.66667"
                        stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_1512_32900">
                    <rect width="20" height="20" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
              <span className="ml-2 text-[16px] text-[#fff] font-[700]">인쇄</span>
            </button>
          </div>
      )}

        {/* 월간 결과 표시 */}
        {!initialLoading && monthlyData && (
            <div className="flex justify-center">
              <div className="w-[1040px] print-area"
                   ref={contentRef}
              >
                <StudentMonthlyReport
                    monthlyData={monthlyData}
                    selectedMonth={selectedMonth}
                    studentInfo={getCurrentStudent()}
                />
              </div>
            </div>
        )}

      </div>
      <SideMenu></SideMenu>
    </>
  );
};

export default StudentMonthlyResultsPage; 